/** Default volumes per spec §11 line 842. Range 0..1. */
export const DEFAULT_VOLUMES = {
  master: 0.7,
  sfx: 0.7,
  music: 0.7,
} as const;

export const VOLUME_STORAGE_KEYS = {
  master: 'smashparty.audio.volume.master',
  sfx: 'smashparty.audio.volume.sfx',
  music: 'smashparty.audio.volume.music',
} as const;

export type VolumeChannel = keyof typeof DEFAULT_VOLUMES;

export interface VolumeController {
  restore(): void;
  set(channel: VolumeChannel, value: number): void;
  get(channel: VolumeChannel): number;
}

export interface VolumeControllerDeps {
  readonly masterGain: GainNode;
  readonly sfxGain: GainNode;
  readonly musicGain: GainNode;
  /** Defaults to `globalThis.localStorage`. Injected for testing. */
  readonly storage?: Pick<Storage, 'getItem' | 'setItem'>;
  /** Defaults to 0. Injected by AudioManager so volume writes can clear pending automation. */
  readonly currentTime?: () => number;
}

const GAIN_FOR_CHANNEL: Record<
  VolumeChannel,
  keyof Pick<VolumeControllerDeps, 'masterGain' | 'sfxGain' | 'musicGain'>
> = {
  master: 'masterGain',
  sfx: 'sfxGain',
  music: 'musicGain',
};

export function createVolumeController(deps: VolumeControllerDeps): VolumeController {
  const storage = deps.storage ?? globalThis.localStorage;
  const currentTime = deps.currentTime ?? (() => 0);

  function gainFor(channel: VolumeChannel): GainNode {
    return deps[GAIN_FOR_CHANNEL[channel]];
  }

  function setGainValue(channel: VolumeChannel, value: number): void {
    const gain = gainFor(channel).gain as AudioParam & {
      cancelScheduledValues?: (cancelTime: number) => AudioParam;
    };
    gain.cancelScheduledValues?.(currentTime());
    gain.value = value;
  }

  return {
    restore(): void {
      for (const channel of Object.keys(DEFAULT_VOLUMES) as VolumeChannel[]) {
        const raw = storage.getItem(VOLUME_STORAGE_KEYS[channel]);
        const parsed = raw !== null ? parseFloat(raw) : NaN;
        const value = Number.isFinite(parsed) ? parsed : DEFAULT_VOLUMES[channel];
        setGainValue(channel, Math.min(1, Math.max(0, value)));
      }
    },

    set(channel: VolumeChannel, value: number): void {
      const clamped = Math.min(1, Math.max(0, value));
      setGainValue(channel, clamped);
      storage.setItem(VOLUME_STORAGE_KEYS[channel], String(clamped));
    },

    get(channel: VolumeChannel): number {
      return gainFor(channel).gain.value;
    },
  };
}
